import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Loader2, MapPin, Clock, ArrowLeft, LogIn, LogOut, User, Car,
  ClipboardList, CheckCircle2, Navigation, Lock, RotateCcw, AlertTriangle, Smile, Info,
  DollarSign, Upload, Trash2, MapPinOff, Flag, Moon, Link2, Users,
} from "lucide-react";
import { toast } from "sonner";
import { ReceiptThumb } from "@/components/ReceiptThumb";
import { Linkify } from "@/components/Linkify";
import { IncidentForm } from "@/components/worker/IncidentForm";
import { ShiftNoteAttachments } from "@/components/ShiftNoteAttachments";
import { LocationMap } from "@/components/MapPin";
import { haversineM, fmtDistance } from "@/lib/geo";
import { useOffline } from "@/context/OfflineContext";
import { isOfflineError, saveDraft, getDrafts, clearDrafts, cacheShift, getCachedShift } from "@/lib/offline";
import { cn } from "@/lib/utils";
import { fmtLog, orgNowStamp } from "@/lib/tz";

const fmtTime = (dt) => (dt ? (String(dt).split(" ")[1] || "").slice(0, 5) : "—");

// Textarea that grows with its content so long notes stay fully visible on a
// phone (fixes the tiny, hard-to-write Events box on mobile).
// Measuring needs height:auto, which briefly collapses the box; the parent is
// pinned to its current height meanwhile so the page never gets shorter, or the
// browser clamps the scroll position and the screen jumps up while typing.
function AutoTextarea({ value, minRows = 2, className, ...props }) {
  const ref = useRef(null);
  const resize = () => {
    const el = ref.current;
    if (!el) return;
    const parent = el.parentElement;
    const y = window.scrollY;
    if (parent) parent.style.minHeight = `${parent.offsetHeight}px`;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
    if (parent) parent.style.minHeight = "";
    if (window.scrollY !== y) window.scrollTo(0, y);
  };
  useEffect(() => { resize(); }, [value]);
  return (
    <Textarea ref={ref} value={value} rows={minRows} onInput={resize}
      className={cn("resize-none overflow-hidden leading-relaxed", className)} {...props} />
  );
}

function GeoTag({ dist, out, unver }) {
  if (unver) return <div className="text-[10px] text-slate-400 mt-1 flex items-center gap-1"><MapPinOff className="h-2.5 w-2.5" /> location unverified</div>;
  if (dist == null) return null;
  return out ? (
    <div className="text-[10px] text-red-300 font-semibold mt-1 flex items-center gap-1" data-testid="geo-out-of-range"><Flag className="h-2.5 w-2.5" /> {fmtDistance(dist)} away · out of range</div>
  ) : (
    <div className="text-[10px] text-emerald-300 mt-1 flex items-center gap-1" data-testid="geo-in-range"><CheckCircle2 className="h-2.5 w-2.5" /> {fmtDistance(dist)} · in range</div>
  );
}

const isIOS = () => typeof navigator !== "undefined" && /iPhone|iPad|iPod/i.test(navigator.userAgent || "");
const isAndroid = () => typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent || "");

// Snapshot of the environment, sent with failed clock attempts so the office can
// tell a per-site denial from an iOS system-level one (Location Services).
async function geoDiagnostics(err) {
  const d = {
    raw_code: err?.code ?? null,
    raw_message: err?.message || null,
    secure_context: typeof window !== "undefined" ? !!window.isSecureContext : null,
    standalone: typeof navigator !== "undefined" ? !!navigator.standalone : null,
    in_iframe: typeof window !== "undefined" ? window.self !== window.top : null,
    online: typeof navigator !== "undefined" ? navigator.onLine : null,
    platform: typeof navigator !== "undefined" ? navigator.platform : null,
    origin: typeof window !== "undefined" ? window.location.origin : null,
  };
  try {
    if (navigator.permissions?.query) {
      d.permission_state = (await navigator.permissions.query({ name: "geolocation" })).state;
    } else {
      d.permission_state = "permissions-api-unavailable";
    }
  } catch (e) { d.permission_state = `query-failed: ${e?.message || e}`; }
  return d;
}

function getPosition() {
  // Try a precise fix first; if the device can't get one (timeout / position
  // unavailable — common indoors or in some in-app browsers), fall back to a
  // low-accuracy fix that accepts a recent cached position. Only a genuine
  // PERMISSION_DENIED (code 1) is reported as a permissions problem.
  const tryOnce = (opts) => new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("Location isn't supported on this device or browser."));
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      (err) => reject(err),
      opts
    );
  });
  const messageFor = (err) => {
    if (err && err.code === 1 && isIOS())
      return "Location is blocked. On iPhone, check Settings → Privacy & Security → Location Services is ON, and Safari Websites is set to 'While Using the App' (not 'Never'). Then fully close this tab, reopen the site and try again.";
    if (err && err.code === 1 && isAndroid())
      return "Location is blocked. On Android, check: (1) Location is ON in your quick settings or Settings → Location; (2) Settings → Apps → your browser (Chrome) → Permissions → Location is 'Allow only while using the app' with Precise on; (3) in Chrome, tap the lock icon next to the address bar → Permissions → Location → Allow. Then reload the page and try again.";
    if (err && err.code === 1)
      return "Location is blocked for this page. Open your browser settings (or phone Settings → Privacy → Location) and allow location for this site, then try again. Tip: if you opened this from an email/app link, open it in Safari or Chrome instead.";
    if (err && err.code === 2 && isIOS())
      return "Couldn't get your location. Check Settings → Privacy & Security → Location Services is ON, then move near a window or outside and try again.";
    if (err && err.code === 2 && isAndroid())
      return "Couldn't get your location. Check Location is ON in your quick settings, then move near a window or outside and try again.";
    if (err && err.code === 2)
      return "Couldn't get a GPS fix right now. Move somewhere with better signal (near a window or outside) and try again.";
    if (err && err.code === 3)
      return "Getting your location took too long. Check your signal and try again.";
    return err?.message || "Couldn't read your location. Please try again.";
  };
  const fail = (err) => { const e = new Error(messageFor(err)); e.code = err?.code ?? null; e.raw = err; return e; };
  return tryOnce({ enableHighAccuracy: true, timeout: 12000, maximumAge: 0 })
    .catch((err) => {
      if (err && err.code === 1) throw fail(err); // denied — no point retrying
      // Timeout / position-unavailable → retry forgivingly: cell/Wi-Fi accuracy
      // (hundreds of metres is fine — the geofence allows for reported accuracy)
      // and accept a fix up to 1 minute old (kept short so the recorded location is current).
      return tryOnce({ enableHighAccuracy: false, timeout: 25000, maximumAge: 60000 })
        .catch((err2) => { throw fail(err2); });
    });
}
const nowStamp = () => orgNowStamp(); // org-timezone wall clock, same as stored shift times

const fmtClock = (d) => (d ? fmtLog(d.toISOString()).split(", ")[1] : "");

// Google-Docs style save status. `st` = {state, at}
function SaveIndicator({ st, className }) {
  if (!st || st.state === "idle") return null;
  const map = {
    pending: { txt: "Saving…", cls: "text-slate-400", spin: true },
    saving: { txt: "Saving…", cls: "text-slate-400", spin: true },
    saved: { txt: `Saved ${fmtClock(st.at)}`, cls: "text-emerald-300", icon: CheckCircle2 },
    offline: { txt: "Saved on device — will sync", cls: "text-amber-300", icon: MapPinOff },
    error: { txt: "Couldn't save — tap Save", cls: "text-red-300", icon: AlertTriangle },
  };
  const c = map[st.state];
  if (!c) return null;
  const Icon = c.icon;
  return (
    <span className={cn("text-[10px] flex items-center gap-1", c.cls, className)} data-testid="save-indicator">
      {c.spin ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : Icon ? <Icon className="h-2.5 w-2.5" /> : null}
      {c.txt}
    </span>
  );
}

const LockedPill = () => (
  <span className="text-[10px] font-semibold text-slate-300 bg-white/10 border border-white/15 px-2 py-0.5 rounded-full flex items-center gap-1" data-testid="locked-pill">
    <Lock className="h-2.5 w-2.5" /> Locked
  </span>
);


export default function WorkerShiftDetail() {
  const { name } = useParams();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [noteMap, setNoteMap] = useState({});
  const [odo, setOdo] = useState({ start: "", end: "", extension_requested: false, extension_contact: "" });
  const [awakeEntries, setAwakeEntries] = useState([]); // saved entries
  const [awakeForm, setAwakeForm] = useState({ start: "", end: "", reason: "", reasonOther: "" }); // new-entry form
  const [expForm, setExpForm] = useState({ amount: "", reason: "", file: null });
  const [taskSel, setTaskSel] = useState({});       // index -> local status override
  const [taskReason, setTaskReason] = useState({}); // index -> not-done reason text
  const [geoConfirm, setGeoConfirm] = useState(null); // {dir, pos, distance, useEnd}

  const load = async () => {
    let d;
    try {
      d = await api.workerShift(name);
      cacheShift(name, d).catch(() => {});
    } catch (e) {
      if (isOfflineError(e)) {
        d = await getCachedShift(name);
        if (!d) throw e;
      } else throw e;
    }
    setS(d);
    const m = {};
    (d.participants || []).forEach((p) => { m[p.participant] = { urgent_notes: "", events: "", mood: "", contacted_office: false, office_contact_reason: "" }; });
    (d.notes || []).forEach((n) => {
      m[n.participant] = { urgent_notes: n.urgent_notes || "", events: n.events || "", mood: n.mood || "", contacted_office: !!n.contacted_office, office_contact_reason: n.office_contact_reason || "" };
    });
    // Overlay any locally-saved draft text for notes that aren't locked yet,
    // so half-typed notes survive navigating away / reopening while offline.
    try {
      const drafts = await getDrafts(name);
      const submitted = new Set((d.notes || []).filter((n) => n.status === "Submitted").map((n) => n.participant));
      Object.entries(drafts).forEach(([pid, fields]) => { if (!submitted.has(pid)) m[pid] = { ...(m[pid] || {}), ...fields }; });
    } catch { /* ignore draft hydration errors */ }
    setNoteMap(m);
    setOdo({
      // Returns 0 (not null) for unset numeric fields; an odometer
      // reading is never legitimately 0, so treat 0/blank as "not entered yet".
      start: Number(d.km_start_odometer) > 0 ? String(d.km_start_odometer) : "",
      end: Number(d.km_end_odometer) > 0 ? String(d.km_end_odometer) : "",
      extension_requested: !!d.km_extension_requested,
      extension_contact: d.km_extension_contact || "",
    });
    const toLocal = (v) => (v ? String(v).replace(" ", "T").slice(0, 16) : "");
    // Hydrate saved awake entries; if the shift only has legacy awake_start/end
    // (no awake_entries array yet), synthesise a single entry so it's visible.
    const serverEntries = Array.isArray(d.awake_entries) ? d.awake_entries : [];
    if (serverEntries.length === 0 && d.awake_start) {
      setAwakeEntries([{
        id: "legacy", start: d.awake_start, end: d.awake_end,
        minutes: d.awake_minutes || 0, reason: "", reason_other: "",
      }]);
    } else {
      setAwakeEntries(serverEntries);
    }
    setAwakeForm({ start: "", end: "", reason: "", reasonOther: "" });
  };

  useEffect(() => {
    load().catch(() => toast.error("Could not load shift.")).finally(() => setLoading(false));
  }, [name]);

  const { enqueue } = useOffline();
  const draftTimers = useRef({});
  const scheduleDraft = (participant, fields) => {
    clearTimeout(draftTimers.current[participant]);
    draftTimers.current[participant] = setTimeout(() => { saveDraft(name, participant, fields).catch(() => {}); }, 400);
  };
  useEffect(() => () => Object.values(draftTimers.current).forEach(clearTimeout), []);

  // ── Auto-save (Google-Docs style) ────────────────────────────────────────
  const [saveStatus, setSaveStatus] = useState({}); // key -> {state, at}
  const autoTimers = useRef({});
  const pendingRef = useRef({});   // key -> () => Promise
  const AUTOSAVE_MS = 1500;

  const flushKey = async (key) => {
    const fn = pendingRef.current[key];
    if (!fn) return;
    delete pendingRef.current[key];
    clearTimeout(autoTimers.current[key]);
    setSaveStatus((st) => ({ ...st, [key]: { state: "saving" } }));
    try {
      if (typeof navigator !== "undefined" && !navigator.onLine) {
        setSaveStatus((st) => ({ ...st, [key]: { state: "offline", at: new Date() } }));
        return;
      }
      await fn();
      setSaveStatus((st) => ({ ...st, [key]: { state: "saved", at: new Date() } }));
    } catch (e) {
      setSaveStatus((st) => ({ ...st, [key]: { state: isOfflineError(e) ? "offline" : "error", at: new Date() } }));
    }
  };

  const queueAuto = (key, fn) => {
    pendingRef.current[key] = fn;
    setSaveStatus((st) => ({ ...st, [key]: { state: "pending" } }));
    clearTimeout(autoTimers.current[key]);
    autoTimers.current[key] = setTimeout(() => flushKey(key), AUTOSAVE_MS);
  };

  const flushAll = () => Object.keys(pendingRef.current).forEach((k) => flushKey(k));
  const cancelKey = (key) => { delete pendingRef.current[key]; clearTimeout(autoTimers.current[key]); };
  const cancelAuto = () => {
    pendingRef.current = {};
    Object.values(autoTimers.current).forEach(clearTimeout);
  };

  // Flush anything still pending when the screen is left or the app backgrounds.
  useEffect(() => {
    const onHide = () => flushAll();
    window.addEventListener("pagehide", onHide);
    document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") onHide(); });
    return () => {
      window.removeEventListener("pagehide", onHide);
      Object.values(autoTimers.current).forEach(clearTimeout);
      flushAll();
    };
  }, [name]);

  // A part is locked once submitted, or (after a partial return) when the office
  // didn't send that part back. Server enforces the same rules.
  const lk = (part) => !!(s?.submitted || s?.locks?.[part]);

  // The office's feedback, shown inside each section it unlocked (km/expenses/tasks/awake/incidents).
  const returnedFeedback = (part) => {
    const ri = s?.return_info;
    if (!ri || !ri.keys?.includes(part)) return null;
    return (
      <div className="mb-3 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3" data-testid={`worker-${part}-returned`}>
        <div className="text-[11px] font-semibold text-amber-300 flex items-center gap-1.5"><RotateCcw className="h-3.5 w-3.5" /> Returned by the office — please update &amp; resubmit</div>
        {ri.reason && <div className="text-[11px] text-amber-200/80 mt-1">"{ri.reason}"</div>}
      </div>
    );
  };

  // Auto-save KM odometer (extension request is never auto-saved — deliberate tap)
  const queueKmAuto = (next) => {
    if (lk("km")) return;
    const start = parseFloat(next.start);
    if (isNaN(start) || start <= 0) return;
    const end = parseFloat(next.end);
    const endValid = String(next.end).trim() !== "" && !isNaN(end) && end >= start;
    queueAuto("km", () => api.workerKm(name, {
      start_odometer: start,
      end_odometer: endValid ? end : null,
      extension_requested: !!s?.km_extension_requested,
      extension_contact: s?.km_extension_contact || "",
    }));
  };

  // New entries are times only ("22:00") — the server dates them from the shift.
  const stamp = (v) => (/^\d{1,2}:\d{2}$/.test(String(v)) ? String(v)
    : String(v).replace("T", " ").padEnd(19, ":00").slice(0, 19));
  const buildAwakePayload = (entries) => ({
    entries: entries.map((e) => ({
      id: e.id || "",
      start: stamp(e.start),
      end: stamp(e.end),
      reason: e.reason || "",
      reason_other: e.reason_other || e.reasonOther || "",
    })),
  });

  const queueAwakeAuto = (entries) => {
    if (lk("awake") || entries.length === 0) return;
    queueAuto("awake", () => api.workerAwake(name, buildAwakePayload(entries)));
  };

  const globalSave = (() => {
    const vals = Object.values(saveStatus);
    if (!vals.length) return null;
    if (vals.some((v) => v.state === "pending" || v.state === "saving")) return { state: "saving" };
    if (vals.some((v) => v.state === "error")) return { state: "error" };
    if (vals.some((v) => v.state === "offline")) return { state: "offline" };
    const last = vals.filter((v) => v.at).sort((a, b) => b.at - a.at)[0];
    return last ? { state: "saved", at: last.at } : null;
  })();

  // Run an action online; if the connection is down (or drops mid-request),
  // queue it encrypted on-device and optimistically patch the UI instead.
  const runOrQueue = async ({ type, payload, apiCall, optimistic, okMsg }) => {
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      await enqueue(type, name, payload);
      optimistic?.();
      toast.success("Saved offline — will sync when you're back online");
      return true;
    }
    try {
      await apiCall();
      if (okMsg) toast.success(okMsg);
      await load();
      return false;
    } catch (e) {
      if (isOfflineError(e)) {
        await enqueue(type, name, payload);
        optimistic?.();
        toast.success("Saved offline — will sync when you're back online");
        return true;
      }
      toast.error(e.response?.data?.detail || e.message || "Something went wrong");
      return false;
    }
  };

  const doClock = async (dir, pos) => {
    setBusy(dir);
    const body = { ...pos, at: nowStamp() };
    await runOrQueue({
      type: dir === "in" ? "clock-in" : "clock-out",
      payload: body,
      apiCall: () => (dir === "in" ? api.workerClockIn(name, body) : api.workerClockOut(name, body)),
      okMsg: dir === "in" ? "Clocked in — location recorded" : "Clocked out — location recorded",
      optimistic: () => setS((cur) => ({ ...cur, [dir === "in" ? "actual_clock_in" : "actual_clock_out"]: body.at })),
    });
    setBusy("");
    setGeoConfirm(null);
  };

  const clock = async (dir) => {
    let pos;
    try {
      pos = await getPosition();
    } catch (e) {
      const codeLabel = { 1: "PERMISSION_DENIED", 2: "POSITION_UNAVAILABLE", 3: "TIMEOUT" }[e.code] || "UNKNOWN";
      geoDiagnostics(e.raw || e).then((diagnostics) => api.workerClockLog({
        shift: name, direction: dir, outcome: "error", phase: "geolocation",
        reason: e.message, error_code: codeLabel, diagnostics,
      })).catch(() => {});
      // Blocked-location errors link straight to the how-to article.
      return toast.error(e.message, e.code === 1
        ? { duration: 15000, action: { label: "How to fix", onClick: () => nav("/worker/help/worker/shifts/clock-in-and-out/location-blocked") } }
        : undefined);
    }
    if (s.geofence_enabled) {
      const useEnd = dir === "out" && s.end_address_enabled && s.end_latitude && s.end_longitude;
      const tlat = useEnd ? s.end_latitude : s.shift_latitude;
      const tlon = useEnd ? s.end_longitude : s.shift_longitude;
      const thr = s.geofence_metres || 500;
      if (tlat && tlon) {
        const dist = haversineM(pos.latitude, pos.longitude, tlat, tlon);
        // Only prompt "out of range" if it's out even after allowing for the
        // device's own location uncertainty (desktop wifi can be very rough).
        const acc = pos.accuracy || 0;
        if (dist - acc > thr) return setGeoConfirm({ dir, pos, distance: dist, useEnd });
      }
    }
    await doClock(dir, pos);
  };

  const saveNote = async (participant) => {
    setBusy(`note-${participant}`);
    const fields = noteMap[participant] || {};
    await runOrQueue({
      type: "note",
      payload: { participant, fields },
      apiCall: () => api.workerSaveNote(name, participant, fields),
      okMsg: "Note saved",
      optimistic: () => setS((cur) => {
        const notes = [...(cur.notes || [])];
        const i = notes.findIndex((n) => n.participant === participant);
        if (i >= 0) notes[i] = { ...notes[i], ...fields, status: notes[i].status === "Submitted" ? "Submitted" : "Draft" };
        else notes.push({ participant, status: "Draft", ...fields });
        return { ...cur, notes };
      }),
    });
    setBusy("");
  };

  const attachInputRefs = useRef({});
  const handleAttach = async (participant, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(`attach-${participant}`);
    try {
      let note = (s.notes || []).find((n) => n.participant === participant);
      let noteName = note?.name;
      if (!noteName) {
        const res = await api.workerSaveNote(name, participant, noteMap[participant] || {});
        noteName = res.name;
      }
      // One file per upload: a big batch never hits the server's request limit,
      // and one file failing doesn't lose the others.
      let added = 0;
      const failed = [];
      for (const f of files) {
        try { await api.workerUploadNoteAttachments(name, noteName, [f]); added += 1; }
        catch (e) { failed.push(e.response?.data?.detail || `Couldn't upload ${f.name}.`); }
      }
      if (added) toast.success(added > 1 ? `${added} attachments added` : "Attachment added");
      if (failed.length) toast.error([...new Set(failed)].join(" "));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't upload that attachment.");
    } finally { setBusy(""); }
  };
  const removeAttach = async (participant, noteName, idx) => {
    setBusy(`attach-${participant}`);
    try {
      await api.workerDeleteNoteAttachment(name, noteName, idx);
      await load();
    } catch { toast.error("Couldn't remove attachment."); }
    finally { setBusy(""); }
  };

  const persistScreen = async () => {
    // Save everything typed on the screen before submitting, so nothing entered is
    // lost if the worker forgot to tap the individual Save buttons first.
    const parts = s.participants || [];
    const hasContent = (f) => f && (String(f.urgent_notes || "").trim() || String(f.events || "").trim()
      || String(f.mood || "").trim() || f.contacted_office || String(f.office_contact_reason || "").trim());
    // 1) Shift notes — save any non-locked participant note with content (or an existing draft being edited)
    for (const p of parts) {
      const note = (s.notes || []).find((n) => n.participant === p.participant);
      if (note?.status === "Submitted") continue; // already locked
      const fields = noteMap[p.participant];
      if (hasContent(fields) || note) await api.workerSaveNote(name, p.participant, fields || {});
    }
    // 2) KM — persist whatever odometer readings are entered
    // (skipped when the office didn't return KM in a partial return — it's locked)
    const start = parseFloat(odo.start);
    if (!isNaN(start) && !lk("km")) {
      const end = parseFloat(odo.end);
      const endValid = String(odo.end).trim() !== "" && !isNaN(end) && end >= start;
      const overAuth = (s?.km_authorised || 0) > 0 && endValid && (end - start) > s.km_authorised;
      await api.workerKm(name, {
        start_odometer: start,
        end_odometer: endValid ? end : null,
        extension_requested: overAuth ? odo.extension_requested : false,
        extension_contact: overAuth ? String(odo.extension_contact || "").trim() : "",
      });
    }
    // 3) Awake time (sleepovers) — save current entries list if any exist
    if (awakeEntries.length > 0 && !lk("awake")) {
      await api.workerAwake(name, buildAwakePayload(awakeEntries));
    }
  };

  const submitTimesheet = async () => {
    if (!s.actual_clock_out) return toast.error("Please clock out before submitting your timesheet.");
    // Every task must be answered (Completed / Not Completed) — with a reason if not done.
    const badTask = (s.tasks || []).findIndex((t) => {
      const status = t.completion_status || (t.completed ? "Completed" : "");
      if (status !== "Completed" && status !== "Not Completed") return true;
      return status === "Not Completed" && !(t.not_done_reason || "").trim();
    });
    if (badTask >= 0) {
      const t = s.tasks[badTask];
      const status = t.completion_status || (t.completed ? "Completed" : "");
      return toast.error(status === "Not Completed"
        ? `Add a reason why "${t.title}" wasn't done.`
        : `Mark the task "${t.title}" as done or not done.`);
    }
    if (typeof navigator !== "undefined" && !navigator.onLine)
      return toast.error("You need a connection to submit your timesheet.");
    const wholePackage = s.is_linked && s.timesheet_status !== "Returned";
    if (wholePackage && !window.confirm(
      `This is a linked package of ${s.leg_count} shifts. Submitting will lock and submit the timesheet for EVERY leg at once. Only continue if you've fully completed the notes, KM and any incidents on all ${s.leg_count} legs.`))
      return;
    setBusy("submit");
    cancelAuto(); // persistScreen below saves everything, so drop queued auto-saves
    try {
      try {
        await persistScreen(); // save typed notes / KM / awake so nothing is lost on submit
      } catch (e) {
        setBusy("");
        return toast.error(e.response?.data?.detail || "Couldn't save your notes and KM before submitting — please try again.");
      }
      const r = await api.workerSubmitNotes(name);
      await clearDrafts(name).catch(() => {});
      toast.success(wholePackage
        ? `Linked package submitted — all ${r.legs_submitted || s.leg_count} legs locked for review`
        : "Timesheet submitted — notes, KM, expenses & incidents are now locked for review");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit timesheet");
    } finally {
      setBusy("");
    }
  };

  const saveKm = async () => {
    cancelKey("km");
    const start = parseFloat(odo.start);
    if (isNaN(start)) return toast.error("Enter the start odometer");
    const endEmpty = String(odo.end).trim() === "";
    const end = parseFloat(odo.end);

    // Draft: start odometer only (logged at the start of the shift). The worker
    // comes back at the end of the shift to add the end reading and complete it.
    if (endEmpty || isNaN(end)) {
      setBusy("km");
      const payload = { start_odometer: start, end_odometer: null, extension_requested: false, extension_contact: "" };
      await runOrQueue({
        type: "km",
        payload,
        apiCall: () => api.workerKm(name, payload),
        okMsg: "Start odometer saved — add the end reading after your shift",
        optimistic: () => setS((cur) => ({ ...cur, km_start_odometer: start, km_end_odometer: null, km_claimed: null })),
      });
      setBusy("");
      return;
    }

    if (end < start) return toast.error("End odometer must be ≥ start");
    const overAuth = (s?.km_authorised || 0) > 0 && (end - start) > s.km_authorised;
    if (overAuth && odo.extension_requested && !odo.extension_contact.trim())
      return toast.error("Who did you speak with about the extension?");
    setBusy("km");
    const payload = {
      start_odometer: start, end_odometer: end,
      extension_requested: overAuth ? odo.extension_requested : false,
      extension_contact: overAuth ? odo.extension_contact.trim() : "",
    };
    await runOrQueue({
      type: "km",
      payload,
      apiCall: () => api.workerKm(name, payload),
      okMsg: "KM claim saved",
      optimistic: () => setS((cur) => ({ ...cur, km_start_odometer: start, km_end_odometer: end, km_claimed: +(end - start).toFixed(1) })),
    });
    setBusy("");
  };

  const AWAKE_REASONS = [
    "Toileting & personal care",
    "Administering medication",
    "Medical crisis",
    "Participant behaviours",
    "Other",
  ];

  const addAwakeEntry = async () => {
    if (!awakeForm.start || !awakeForm.end) return toast.error("Enter both the awake start and end time.");
    if (awakeForm.end === awakeForm.start) return toast.error("The end time must be different from the start time.");
    if (!awakeForm.reason) return toast.error("Select a reason for being awake.");
    if (awakeForm.reason === "Other" && !awakeForm.reasonOther.trim()) return toast.error("Describe why you were awake.");
    if (typeof navigator !== "undefined" && !navigator.onLine)
      return toast.error("You need a connection to save awake time.");
    const newEntry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      start: awakeForm.start,
      end: awakeForm.end,
      reason: awakeForm.reason,
      reason_other: awakeForm.reason === "Other" ? awakeForm.reasonOther.trim() : "",
    };
    const next = [...awakeEntries, newEntry];
    cancelKey("awake");
    setBusy("awake");
    try {
      await api.workerAwake(name, buildAwakePayload(next));
      toast.success("Awake time entry saved — the office will confirm it.");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save awake time.");
    } finally { setBusy(""); }
  };

  const removeAwakeEntry = async (id) => {
    const next = awakeEntries.filter((e) => e.id !== id);
    cancelKey("awake");
    setBusy("awake");
    try {
      await api.workerAwake(name, buildAwakePayload(next));
      if (next.length === 0) toast.success("Awake time cleared.");
      else toast.success("Entry removed.");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update awake time.");
    } finally { setBusy(""); }
  };

  const clearAwake = async () => {
    cancelKey("awake");
    setBusy("awake");
    try {
      await api.workerAwake(name, { entries: [] });
      setAwakeEntries([]);
      setAwakeForm({ start: "", end: "", reason: "", reasonOther: "" });
      toast.success("Awake time cleared.");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not clear awake time."); }
    finally { setBusy(""); }
  };

  const addExpense = async () => {
    const amt = parseFloat(expForm.amount);
    if (isNaN(amt) || amt <= 0) return toast.error("Enter a valid amount");
    if (!expForm.reason.trim()) return toast.error("Add a reason");
    if (!expForm.file) return toast.error("Attach a receipt");
    if (typeof navigator !== "undefined" && !navigator.onLine)
      return toast.error("Expense claims need a connection to upload the receipt. Try again once you're back online.");
    setBusy("expense");
    try {
      const fd = new FormData();
      fd.append("amount", String(amt));
      fd.append("reason", expForm.reason.trim());
      fd.append("receipt", expForm.file);
      await api.workerAddExpense(name, fd);
      toast.success("Expense claim submitted");
      setExpForm({ amount: "", reason: "", file: null });
      await load();
    } catch (e) {
      toast.error(isOfflineError(e) ? "Expense claims need a connection to upload the receipt." : (e.response?.data?.detail || "Could not submit expense"));
    } finally {
      setBusy("");
    }
  };

  const deleteExpense = async (idx) => {
    if (typeof navigator !== "undefined" && !navigator.onLine)
      return toast.error("You need a connection to remove an expense claim.");
    try {
      await api.workerDeleteExpense(name, idx);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove expense");
    }
  };

  const setTaskStatus = async (i, status, reason) => {
    await runOrQueue({
      type: "task",
      payload: { index: i, completion_status: status, not_done_reason: reason || "" },
      apiCall: () => api.workerTask(name, i, status, reason || ""),
      optimistic: () => setS((cur) => {
        const tasks = [...(cur.tasks || [])];
        if (tasks[i]) tasks[i] = { ...tasks[i], completion_status: status, completed: status === "Completed", not_done_reason: status === "Completed" ? "" : (reason || "") };
        return { ...cur, tasks };
      }),
    });
  };

  if (loading || !s)
    return <div className="flex justify-center pt-24"><Loader2 className="h-6 w-6 animate-spin text-[#00c86e]" /></div>;

  const pd = s.participant_detail || {};

  return (
    <div className="px-4 pt-4 pb-6" data-testid="worker-shift-detail">
      <button onClick={() => nav("/worker")} className="flex items-center gap-1 text-slate-400 text-xs mb-3" data-testid="worker-shift-back">
        <ArrowLeft className="h-4 w-4" /> Roster
      </button>

      {!s.submitted && globalSave && (
        <div className="sticky top-0 z-30 -mx-4 px-4 py-1.5 mb-2 bg-[#0b1220]/90 backdrop-blur border-b border-white/5 flex justify-end"
          data-testid="worker-autosave-bar">
          <SaveIndicator st={globalSave} className="text-[11px]" />
        </div>
      )}

      {(() => {
        const rn = (s.notes || []).filter((n) => n.status === "Returned");
        const ri = (s.incidents || []).filter((i) => i.status === "Returned");
        const flagged = s.timesheet_status === "Flagged";
        const rp = s.return_info;
        if (!rn.length && !ri.length && !flagged && !rp) return null;
        return (
          <div className="mb-3 rounded-2xl border border-amber-500/50 bg-amber-500/15 p-3.5" data-testid="worker-returned-banner">
            <div className="text-[11px] font-bold uppercase tracking-wider text-amber-300 flex items-center gap-1.5">
              <RotateCcw className="h-3.5 w-3.5" /> Returned by the office — action needed
            </div>
            <ul className="mt-2 space-y-1.5">
              {flagged && <li className="text-[12px] text-amber-100"><span className="font-semibold">Timesheet:</span> {s.timesheet_flag_reason || "please review"}</li>}
              {rp && <li className="text-[12px] text-amber-100"><span className="font-semibold">{rp.parts.join(", ")}:</span> {rp.reason || "please update"}</li>}
              {rn.map((n) => <li key={n.name} className="text-[12px] text-amber-100"><span className="font-semibold">Shift notes{n.participant_name ? ` · ${n.participant_name}` : ""}:</span> {n.returned_reason || "please update"}</li>)}
              {ri.map((i) => <li key={i.name} className="text-[12px] text-amber-100"><span className="font-semibold">Incident {i.name}:</span> {i.return_reason || "please update"}</li>)}
            </ul>
            <div className="text-[10px] text-amber-200/70 mt-2">Only the returned items are unlocked for editing — everything else stays locked.</div>
          </div>
        );
      })()}

      {/* Hero */}
      <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
        <div className="flex items-center gap-2 text-white text-lg font-bold">
          <Clock className="h-5 w-5 text-[#00c86e]" />
          {fmtTime(s.start_time)} – {fmtTime(s.end_time)}
          <span className="text-slate-400 text-sm font-normal ml-1">· {s.rostered_hours}h</span>
        </div>
        <div className="mt-1.5 text-white/90 font-medium">{s.participant_name}</div>
        <div className="mt-0.5 text-[10px] text-slate-500 font-mono" data-testid="worker-shift-id">{name}</div>
        {s.location && (
          <div className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-400">
            <MapPin className="h-3.5 w-3.5" /> {s.location}
          </div>
        )}
        {s.end_address_enabled && s.end_address && (
          <div className="mt-1 flex items-start gap-1.5 text-xs text-amber-300/90" data-testid="worker-end-address">
            <Navigation className="h-3.5 w-3.5 mt-0.5" /> <span>Ends at: {s.end_address}</span>
          </div>
        )}
        {s.shift_latitude && s.shift_longitude && (
          <LocationMap lat={s.shift_latitude} lng={s.shift_longitude} label="Shift location" height={140} className="mt-3" />
        )}
      </div>

      {/* Other workers on this shift */}
      {(s.coworkers || []).length > 0 && (
        <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-4" data-testid="worker-coworkers">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-emerald-300/80 mb-1.5">
            <Users className="h-3.5 w-3.5" /> Working with
          </div>
          <div className="text-sm text-white/90">
            {s.coworkers.map((c) => c.name + (c.is_lead ? " (lead)" : "")).join(", ")}
          </div>
          <div className="text-[11px] text-slate-400 mt-1.5" data-testid="worker-own-timesheet-note">
            You clock in and out and submit your own notes and timesheet.
          </div>
        </div>
      )}

      {/* Linked package — rope tying the legs together */}
      {s.is_linked && (
        <div className="mt-4 rounded-2xl border border-indigo-400/30 bg-indigo-500/10 p-4" data-testid="worker-linked-package">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-indigo-300 mb-1">
            <Link2 className="h-3.5 w-3.5" /> Linked Package · {s.leg_count} shifts
          </div>
          <p className="text-[11px] text-indigo-100/80 mb-3">
            These run back-to-back as one job. Clock in on the first leg and out on the last — your time runs across all of them. Complete each leg's notes, KM &amp; incidents; submitting the final leg submits them all.
          </p>
          <div className="relative pl-5">
            <div className="absolute left-[7px] top-2 bottom-2 w-0.5 bg-indigo-400/40" />
            {(s.linked_legs || []).map((l, i) => {
              const isMe = l.name === s.name;
              return (
                <button key={l.name} onClick={() => !isMe && nav(`/worker/shift/${l.name}`)} disabled={isMe}
                  data-testid={`worker-linked-leg-${i + 1}`}
                  className={`relative flex items-center gap-2 w-full text-left py-1.5 ${isMe ? "" : "opacity-80 hover:opacity-100"}`}>
                  <span className={`absolute -left-5 h-3 w-3 rounded-full border-2 ${isMe ? "bg-indigo-400 border-indigo-200" : "bg-slate-800 border-indigo-400/60"}`} />
                  <span className={`text-xs ${isMe ? "text-white font-semibold" : "text-indigo-100/70"}`}>
                    Leg {l.link_seq}: {fmtTime(l.start_time)}–{fmtTime(l.end_time)}
                    {l.participant_name ? ` · ${l.participant_name}` : ""}
                    {isMe ? " (this leg)" : ""}
                  </span>
                  {l.clocked_out ? <CheckCircle2 className="h-3 w-3 text-emerald-400" /> : null}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Clock in/out */}
      <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-4">
        <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-emerald-300/80 mb-3">
          <Navigation className="h-3.5 w-3.5" /> Clock & Location
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div className="rounded-xl bg-black/20 p-3">
            <div className="text-[10px] text-slate-500 uppercase">Clock in</div>
            <div className="text-white font-semibold mt-0.5">{fmtTime(s.actual_clock_in)}</div>
            {s.actual_clock_in && <GeoTag dist={s.clock_in_distance} out={s.clock_in_out_of_range} unver={s.geofence_unverifiable} />}
          </div>
          <div className="rounded-xl bg-black/20 p-3">
            <div className="text-[10px] text-slate-500 uppercase">Clock out</div>
            <div className="text-white font-semibold mt-0.5">{fmtTime(s.actual_clock_out)}</div>
            {s.actual_clock_out && <GeoTag dist={s.clock_out_distance} out={s.clock_out_out_of_range} unver={s.geofence_unverifiable} />}
          </div>
        </div>
        {!s.actual_clock_in ? (
          s.is_linked && !s.is_first_leg ? (
            <button onClick={() => nav(`/worker/shift/${s.first_leg}`)} data-testid="worker-clock-goto-first"
              className="w-full h-11 rounded-md font-medium bg-indigo-500/15 text-indigo-200 border border-indigo-400/30 text-sm">
              Clock in on the first leg (leg 1) →
            </button>
          ) : (
            <Button onClick={() => clock("in")} disabled={busy === "in"} data-testid="worker-clock-in-btn"
              className="w-full h-11 font-semibold bg-emerald-600 hover:bg-emerald-700">
              {busy === "in" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LogIn className="h-4 w-4 mr-2" /> Clock In{s.is_linked ? " (starts the package)" : ""}</>}
            </Button>
          )
        ) : !s.actual_clock_out ? (
          s.is_linked && !s.is_last_leg ? (
            <button onClick={() => nav(`/worker/shift/${s.last_leg}`)} data-testid="worker-clock-goto-last"
              className="w-full h-11 rounded-md font-medium bg-indigo-500/15 text-indigo-200 border border-indigo-400/30 text-sm">
              Clock out from the final leg (leg {s.leg_count}) →
            </button>
          ) : (
            <Button onClick={() => clock("out")} disabled={busy === "out"} data-testid="worker-clock-out-btn"
              className="w-full h-11 font-semibold bg-brand hover:bg-brand-dark">
              {busy === "out" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LogOut className="h-4 w-4 mr-2" /> Clock Out{s.is_linked ? " (ends the package)" : ""}</>}
            </Button>
          )
        ) : (
          <div className="flex items-center justify-center gap-2 text-emerald-400 text-sm font-medium py-2">
            <CheckCircle2 className="h-4 w-4" /> Shift complete — submitted for review
          </div>
        )}
        <p className="text-[10px] text-slate-500 mt-2 text-center">Your GPS location is captured at clock in/out for compliance.</p>
      </div>

      {/* Shift instructions */}
      {s.shift_instructions && (
        <div className="mt-4 rounded-2xl border border-emerald-500/25 bg-emerald-500/10 p-4" data-testid="worker-shift-instructions">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-[#00c86e] mb-2">
            <Info className="h-3.5 w-3.5" /> Shift Instructions
          </div>
          <Linkify text={s.shift_instructions} className="block text-sm text-emerald-50/90 leading-relaxed"
            linkClassName="underline font-medium text-white break-words" />
        </div>
      )}

      {/* Tasks */}
      {s.tasks?.length > 0 && (
        <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-4">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-emerald-300/80 mb-3">
            <ClipboardList className="h-3.5 w-3.5" /> Tasks
          </div>
          {returnedFeedback("tasks")}
          <div className="space-y-3">
            {s.tasks.map((t, i) => {
              const persisted = t.completion_status || (t.completed ? "Completed" : "");
              const sel = taskSel[i] !== undefined ? taskSel[i] : persisted;
              const reasonVal = taskReason[i] !== undefined ? taskReason[i] : (t.not_done_reason || "");
              const locked = lk("tasks");
              const savedReason = persisted === "Not Completed" ? (t.not_done_reason || "") : "";
              const reasonDirty = reasonVal.trim() !== savedReason;
              const reasonSaved = !!savedReason && !reasonDirty;
              return (
                <div key={i} data-testid={`worker-task-${i}`} className="rounded-xl bg-black/20 border border-white/10 p-3">
                  <div className={`text-sm font-medium ${persisted === "Completed" ? "text-slate-400" : "text-white/90"}`}>{t.title}</div>
                  {t.notes && <div className="text-[11px] text-slate-500 mt-0.5">{t.notes}</div>}
                  <div className="text-[11px] text-slate-400 mt-2 mb-1.5">Task completed?</div>
                  <div className="flex gap-2">
                    {[["Yes", "Completed"], ["No", "Not Completed"]].map(([lbl, val]) => (
                      <button key={val} type="button" disabled={locked}
                        data-testid={`worker-task-${i}-${lbl.toLowerCase()}`}
                        onClick={() => {
                          setTaskSel((m) => ({ ...m, [i]: val }));
                          if (val === "Completed") { setTaskReason((m) => ({ ...m, [i]: "" })); setTaskStatus(i, "Completed", ""); }
                          else if (taskReason[i] === undefined) setTaskReason((m) => ({ ...m, [i]: t.not_done_reason || "" }));
                        }}
                        className={`text-xs font-semibold px-5 py-1.5 rounded-md border transition-colors disabled:opacity-60 ${
                          sel === val
                            ? (val === "Completed" ? "bg-emerald-600 text-white border-emerald-600" : "bg-red-500/90 text-white border-red-500")
                            : "bg-transparent text-slate-300 border-white/15"}`}>
                        {lbl}
                      </button>
                    ))}
                  </div>
                  {sel === "Not Completed" && (
                    <div className="mt-2.5" data-testid={`worker-task-${i}-reason-block`}>
                      <div className="text-[11px] font-semibold text-red-300 mb-1">Why wasn't it completed? <span className="text-red-400">*</span></div>
                      <Textarea value={reasonVal} disabled={locked} rows={2}
                        onChange={(e) => setTaskReason((m) => ({ ...m, [i]: e.target.value }))}
                        data-testid={`worker-task-${i}-reason`}
                        placeholder="Explain why this task couldn't be completed…"
                        className="bg-black/20 border-white/10 text-white placeholder:text-slate-500 text-sm disabled:opacity-70" />
                      {!locked && (
                        <div className="flex items-center gap-2 mt-2">
                          <Button onClick={() => reasonVal.trim() ? setTaskStatus(i, "Not Completed", reasonVal.trim()) : toast.error("A reason is required")}
                            disabled={!reasonVal.trim() || !reasonDirty} data-testid={`worker-task-${i}-reason-save`}
                            variant="outline" className="h-8 text-xs font-semibold bg-transparent border-white/15 text-white hover:bg-white/10">
                            {reasonSaved ? "Saved" : "Save reason"}
                          </Button>
                          {reasonSaved && <span className="text-[10px] text-emerald-300 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Recorded</span>}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Shift notes — one per participant, submit to lock */}
      {(() => {
        const parts = s.participants || [];
        const noteByPart = {};
        (s.notes || []).forEach((n) => { noteByPart[n.participant] = n; });
        const anySubmitted = (s.notes || []).some((n) => n.status === "Submitted");
        const allSubmitted = parts.length > 0 && parts.every((p) => noteByPart[p.participant]?.status === "Submitted");
        const returned = (s.notes || []).find((n) => n.status === "Returned");
        return (
          <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-4" data-testid="worker-notes-section">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-300/80">Shift Notes</div>
              {allSubmitted
                ? <span className="text-[10px] font-semibold text-emerald-300 bg-emerald-500/15 border border-emerald-500/30 px-2 py-0.5 rounded-full flex items-center gap-1"><Lock className="h-2.5 w-2.5" /> Submitted</span>
                : <span className="text-[10px] text-slate-500">Saves automatically as you type</span>}
            </div>

            {returned && (
              <div className="mb-3 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3" data-testid="worker-notes-returned">
                <div className="text-[11px] font-semibold text-amber-300 flex items-center gap-1.5"><RotateCcw className="h-3.5 w-3.5" /> Returned by the office — please update &amp; resubmit</div>
                {returned.returned_reason && <div className="text-[11px] text-amber-200/80 mt-1">"{returned.returned_reason}"</div>}
              </div>
            )}

            <div className="space-y-4">
              {parts.map((p) => {
                const n = noteByPart[p.participant];
                const locked = n?.status === "Submitted";
                const val = noteMap[p.participant] || { urgent_notes: "", events: "", mood: "", contacted_office: false, office_contact_reason: "" };
                const setField = (k, v) => {
                  const next = { ...val, [k]: v };
                  setNoteMap((m) => ({ ...m, [p.participant]: next }));
                  scheduleDraft(p.participant, next);
                  if (!locked) queueAuto(`note-${p.participant}`, () => api.workerSaveNote(name, p.participant, next));
                };
                const ta = (k, ph, rows) => (
                  <AutoTextarea value={val[k] || ""} disabled={locked} minRows={rows}
                    onChange={(e) => setField(k, e.target.value)}
                    onBlur={() => flushKey(`note-${p.participant}`)}
                    data-testid={`worker-note-${k}-${p.participant}`} placeholder={ph}
                    className="bg-black/20 border-white/10 text-white placeholder:text-slate-500 text-sm disabled:opacity-70" />
                );
                return (
                  <div key={p.participant} data-testid={`worker-note-${p.participant}`} className="space-y-3">
                    {parts.length > 1 && <div className="text-xs text-white/80 font-semibold border-b border-white/10 pb-1.5">{p.participant_name} <span className="text-slate-500 font-normal">· one note required per participant</span></div>}
                    <div>
                      <div className="flex items-center gap-1.5 text-[11px] font-semibold text-amber-300 mb-1">
                        <AlertTriangle className="h-3 w-3" /> Important Notes <span className="text-slate-500 font-normal">(optional)</span>
                      </div>
                      <div className="text-[10px] text-slate-500 mb-1">Flag upcoming appointments and any noticeable changes.</div>
                      {ta("urgent_notes", "Upcoming appointments, noticeable changes, anything the office / next worker must know…", 2)}
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5 text-[11px] font-semibold text-white mb-1">
                        Events <span className="text-red-400">*</span> <span className="text-slate-500 font-normal">(required)</span>
                      </div>
                      {ta("events", "What happened during the shift — activities, tasks, meals, appointments…", 4)}
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5 text-[11px] font-semibold text-emerald-200 mb-1">
                        <Smile className="h-3 w-3" /> Mood <span className="text-slate-500 font-normal">(optional)</span>
                      </div>
                      {ta("mood", "How were they in themselves — mood, behaviour, wellbeing…", 2)}
                    </div>
                    <div>
                      <div className="text-[11px] font-semibold text-white mb-1.5">Did you contact the office during the shift?</div>
                      <div className="flex gap-2">
                        {[["Yes", true], ["No", false]].map(([lbl, v]) => (
                          <button key={lbl} type="button" disabled={locked}
                            onClick={() => setField("contacted_office", v)}
                            data-testid={`worker-note-contact-${v ? "yes" : "no"}-${p.participant}`}
                            className={`text-xs font-semibold px-4 py-1.5 rounded-md border transition-colors disabled:opacity-60 ${val.contacted_office === v ? (v ? "bg-brand text-white border-brand" : "bg-white/10 text-white border-white/20") : "bg-transparent text-slate-300 border-white/15"}`}>
                            {lbl}
                          </button>
                        ))}
                      </div>
                      {val.contacted_office === true && (
                        <Textarea value={val.office_contact_reason || ""} disabled={locked} rows={2}
                          onChange={(e) => setField("office_contact_reason", e.target.value)}
                          onBlur={() => flushKey(`note-${p.participant}`)}
                          data-testid={`worker-note-contact-reason-${p.participant}`}
                          placeholder="What did you contact the office about? (required)"
                          className="mt-2 bg-black/20 border-white/10 text-white placeholder:text-slate-500 text-sm disabled:opacity-70" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5 text-[11px] font-semibold text-white mb-1">
                        <Upload className="h-3 w-3" /> Attachments <span className="text-slate-500 font-normal">(optional)</span>
                      </div>
                      <div className="text-[10px] text-slate-500 mb-1.5">Photos or documents (PDF, Word, Excel…). Max 15MB each.</div>
                      <ShiftNoteAttachments
                        items={n?.attachments || []}
                        load={(idx, preview) => api.workerNoteAttachment(name, n.name, idx, preview)}
                        onRemove={locked ? undefined : (n?.name ? (idx) => removeAttach(p.participant, n.name, idx) : undefined)}
                        dark />
                      {!locked && (
                        <>
                          <input ref={(el) => (attachInputRefs.current[p.participant] = el)} type="file" multiple
                            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf"
                            className="hidden" data-testid={`worker-note-attach-input-${p.participant}`}
                            onChange={(e) => { handleAttach(p.participant, e.target.files); e.target.value = ""; }} />
                          <Button type="button" variant="outline" onClick={() => attachInputRefs.current[p.participant]?.click()}
                            disabled={busy === `attach-${p.participant}`} data-testid={`worker-note-attach-${p.participant}`}
                            className="mt-2 h-8 text-xs font-semibold bg-transparent border-white/15 text-white hover:bg-white/10">
                            {busy === `attach-${p.participant}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-3.5 w-3.5 mr-1.5" />Add attachment</>}
                          </Button>
                        </>
                      )}
                    </div>
                    {!locked && (
                      <div className="flex items-center gap-2">
                        <Button onClick={() => { cancelKey(`note-${p.participant}`); saveNote(p.participant); }} disabled={busy === `note-${p.participant}`}
                          data-testid={`worker-note-save-${p.participant}`} variant="outline"
                          className="h-9 text-xs font-semibold bg-transparent border-white/15 text-white hover:bg-white/10">
                          {busy === `note-${p.participant}` ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save draft"}
                        </Button>
                        <SaveIndicator st={saveStatus[`note-${p.participant}`]} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {!allSubmitted && (
              <p className="text-[10px] text-slate-500 mt-3 text-center">
                Save your notes as a draft. When everything's done, use <span className="text-white font-semibold">Submit Timesheet</span> at the bottom to send them for review.
              </p>
            )}
            {allSubmitted && (
              <p className="text-[10px] text-slate-500 mt-2 text-center">
                Submitted for review. The office can return them if changes are needed.
              </p>
            )}
          </div>
        );
      })()}

      {/* Awake time — sleepover only */}
      {!!s.is_sleepover && (() => {
        const totalMins = awakeEntries.reduce((sum, e) => sum + (e.minutes || 0), 0);
        return (
          <div className="mt-4 rounded-2xl border border-indigo-400/20 bg-indigo-500/10 p-4" data-testid="worker-awake-section">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-indigo-200/80">
                <Moon className="h-3.5 w-3.5" /> Active Time
              </div>
              {lk("awake") ? <LockedPill /> : <SaveIndicator st={saveStatus.awake} />}
            </div>
          {returnedFeedback("awake")}
            <p className="text-[11px] text-slate-300/80 mb-3 leading-relaxed">
              Log anytime you were awake during your inactive period and providing direct support to the participant.
              The office will confirm this in your shift notes before approving.
            </p>
            {/* ── Saved entries list ── */}
            {awakeEntries.length > 0 && (
              <div className="mb-3 space-y-2" data-testid="worker-awake-entries">
                {awakeEntries.map((entry, idx) => {
                  const entryMins = entry.minutes || (() => {
                    const a = new Date(String(entry.start).replace(" ", "T"));
                    const b = new Date(String(entry.end).replace(" ", "T"));
                    return b > a ? Math.round((b - a) / 60000) : 0;
                  })();
                  const reasonLabel = entry.reason
                    ? (entry.reason === "Other" && entry.reason_other ? entry.reason_other : entry.reason)
                    : "No reason recorded";
                  return (
                    <div key={entry.id || idx} className="flex items-start justify-between gap-2 rounded-xl bg-indigo-600/20 border border-indigo-400/20 px-3 py-2" data-testid={`worker-awake-entry-${idx}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] font-semibold text-indigo-100">
                          {String(entry.start).slice(11, 16)}–{String(entry.end).slice(11, 16)}
                          <span className="ml-2 font-normal text-indigo-200/70">({entryMins} min)</span>
                        </div>
                        <div className="text-[11px] text-slate-300/80 mt-0.5 truncate">{reasonLabel}</div>
                      </div>
                      {!lk("awake") && (
                        <button type="button" onClick={() => removeAwakeEntry(entry.id || String(idx))}
                          disabled={busy === "awake"} data-testid={`worker-awake-remove-${idx}`}
                          className="text-[10px] text-slate-400 hover:text-red-300 shrink-0 mt-0.5 transition-colors" aria-label="Remove entry">
                          Remove
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {/* ── Total summary ── */}
            {totalMins > 0 && (
              <div className="mb-3 text-[11px] text-indigo-100/90" data-testid="worker-awake-summary">
                {Math.round(totalMins)} min total
              </div>
            )}
            {/* ── Office confirmation status ── */}
            {awakeEntries.length > 0 && (
              <div className="mb-3 text-[11px]" data-testid="worker-awake-status">
                {s.awake_approved
                  ? <span className="text-emerald-300">✓ Confirmed by the office</span>
                  : lk("awake")
                    ? (
                      <div>
                        <span className="text-red-400 font-semibold">✗ Not confirmed by the office</span>
                        {s.awake_reject_reason && (
                          <div className="mt-1 rounded-lg border border-red-400/20 bg-red-500/10 px-3 py-2 text-[11px] text-red-200 leading-relaxed" data-testid="worker-awake-reject-reason">
                            <span className="font-semibold text-red-300">Reason: </span>{s.awake_reject_reason}
                          </div>
                        )}
                        <div className="text-slate-400 mt-1">Please contact your supervisor if you believe this is incorrect.</div>
                      </div>
                    )
                    : <span className="text-amber-300">Awaiting office confirmation</span>}
                {s.awake_adjusted_by && s.awake_approved && <span className="text-slate-400"> · adjusted by the office</span>}
              </div>
            )}
            {/* ── Add-entry form ── */}
            {!lk("awake") && (
              <div className="border-t border-indigo-400/15 pt-3 mt-1 space-y-2">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-indigo-200/60 mb-1">Add awake period</div>
                <div className="text-[10px] text-slate-400 -mt-1 mb-1">Just enter the times. Times after midnight are counted on the next morning of this shift.</div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-[10px] text-slate-400 mb-1">Awake from</div>
                    <Input type="time" step="300" disabled={busy === "awake"} value={awakeForm.start}
                      onChange={(e) => setAwakeForm((f) => ({ ...f, start: e.target.value }))}
                      data-testid="worker-awake-start" className="bg-black/20 border-white/10 text-white disabled:opacity-60" />
                  </div>
                  <div>
                    <div className="text-[10px] text-slate-400 mb-1">Awake to</div>
                    <Input type="time" step="300" disabled={busy === "awake"} value={awakeForm.end}
                      onChange={(e) => setAwakeForm((f) => ({ ...f, end: e.target.value }))}
                      data-testid="worker-awake-end" className="bg-black/20 border-white/10 text-white disabled:opacity-60" />
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-slate-400 mb-1">Reason awake</div>
                  <select value={awakeForm.reason}
                    onChange={(e) => setAwakeForm((f) => ({ ...f, reason: e.target.value, reasonOther: "" }))}
                    disabled={busy === "awake"} data-testid="worker-awake-reason"
                    className="w-full rounded-md border border-white/10 bg-black/20 text-white text-[13px] px-3 py-2 disabled:opacity-60 focus:outline-none focus:ring-1 focus:ring-indigo-400">
                    <option value="">Select a reason…</option>
                    {AWAKE_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                {awakeForm.reason === "Other" && (
                  <div>
                    <div className="text-[10px] text-slate-400 mb-1">Please describe</div>
                    <Input type="text" placeholder="Describe the reason…" value={awakeForm.reasonOther}
                      onChange={(e) => setAwakeForm((f) => ({ ...f, reasonOther: e.target.value }))}
                      disabled={busy === "awake"} data-testid="worker-awake-reason-other"
                      className="bg-black/20 border-white/10 text-white placeholder:text-slate-500 disabled:opacity-60" />
                  </div>
                )}
                <div className="flex gap-2 pt-1">
                  <Button onClick={addAwakeEntry} disabled={busy === "awake"} data-testid="worker-awake-save"
                    className="flex-1 bg-indigo-500 hover:bg-indigo-600 text-white h-10 rounded-xl text-sm font-semibold">
                    {busy === "awake" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add entry"}
                  </Button>
                  {awakeEntries.length > 0 && (
                    <Button onClick={clearAwake} disabled={busy === "awake"} variant="outline" data-testid="worker-awake-clear"
                      className="h-10 rounded-xl text-sm border-white/15 bg-white/5 text-slate-200 hover:bg-white/10">Clear all</Button>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* KM claim — odometer */}
      {(() => {
        const start = parseFloat(odo.start), end = parseFloat(odo.end);
        const endEmpty = String(odo.end).trim() === "" || isNaN(end);
        const computed = !isNaN(start) && !isNaN(end) && end >= start ? +(end - start).toFixed(1) : null;
        const overAuth = (s.km_authorised || 0) > 0 && computed != null && computed > s.km_authorised;
        const startSaved = Number(s.km_start_odometer) > 0 && !(Number(s.km_end_odometer) > 0);
        return (
          <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-4" data-testid="worker-km-section">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-emerald-300/80">
                <Car className="h-3.5 w-3.5" /> KM Claim
              </div>
              {lk("km") ? <LockedPill /> : <SaveIndicator st={saveStatus.km} />}
            </div>
          {returnedFeedback("km")}
            {s.km_authorised > 0 && <div className="text-[11px] text-slate-400 mb-2">Authorised up to {s.km_authorised} km</div>}
            {startSaved && !lk("km") && (
              <div className="mb-2 text-[11px] text-emerald-300 flex items-center gap-1.5" data-testid="worker-km-start-saved">
                <CheckCircle2 className="h-3.5 w-3.5" /> Start odometer saved — add your end reading when the shift wraps up.
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-[10px] text-slate-500 mb-1">Start odometer</div>
                <Input type="number" step="0.1" min="0" disabled={lk("km")} value={odo.start}
                  onChange={(e) => setOdo((o) => { const n = { ...o, start: e.target.value }; queueKmAuto(n); return n; })}
                  onBlur={() => flushKey("km")}
                  data-testid="worker-odo-start" placeholder="e.g. 10000"
                  className="bg-black/20 border-white/10 text-white placeholder:text-slate-500 disabled:opacity-60" />
              </div>
              <div>
                <div className="text-[10px] text-slate-500 mb-1">End odometer <span className="text-slate-600">(at end of shift)</span></div>
                <Input type="number" step="0.1" min="0" disabled={lk("km")} value={odo.end}
                  onChange={(e) => setOdo((o) => { const n = { ...o, end: e.target.value }; queueKmAuto(n); return n; })}
                  onBlur={() => flushKey("km")}
                  data-testid="worker-odo-end" placeholder="e.g. 10035"
                  className="bg-black/20 border-white/10 text-white placeholder:text-slate-500 disabled:opacity-60" />
              </div>
            </div>
            {computed != null && (
              <div className="mt-2 text-sm text-white font-semibold" data-testid="worker-km-computed">= {computed} km</div>
            )}
            {overAuth && (
              <div className="mt-3 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3" data-testid="worker-km-over">
                <div className="text-[11px] font-semibold text-amber-300 flex items-center gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5" /> This exceeds the authorised {s.km_authorised} km
                </div>
                <div className="text-[11px] text-amber-200/90 mt-2">Have you contacted the office to request an extension?</div>
                <div className="flex gap-2 mt-2">
                  {[["yes", true], ["no", false]].map(([lbl, v]) => (
                    <button key={lbl} disabled={lk("km")} onClick={() => setOdo((o) => ({ ...o, extension_requested: v }))}
                      data-testid={`worker-km-ext-${lbl}`}
                      className={`text-xs font-semibold px-3 py-1.5 rounded-md border transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${odo.extension_requested === v ? "bg-amber-500 text-black border-amber-500" : "bg-transparent text-amber-200 border-amber-500/40"}`}>
                      {v ? "Yes" : "No"}
                    </button>
                  ))}
                </div>
                {odo.extension_requested && (
                  <Input value={odo.extension_contact} disabled={lk("km")} onChange={(e) => setOdo((o) => ({ ...o, extension_contact: e.target.value }))}
                    data-testid="worker-km-ext-contact" placeholder="Who did you speak with?"
                    className="mt-2 bg-black/20 border-amber-500/30 text-white placeholder:text-amber-200/40 text-sm disabled:opacity-60" />
                )}
              </div>
            )}
            {!lk("km") && (
              <>
                <Button onClick={saveKm} disabled={busy === "km"} data-testid="worker-km-save"
                  className="mt-3 w-full h-10 font-semibold bg-brand hover:bg-brand-dark">
                  {busy === "km" ? <Loader2 className="h-4 w-4 animate-spin" /> : (endEmpty ? "Save start odometer" : "Save KM Claim")}
                </Button>
                <div className="mt-1.5 flex justify-center"><SaveIndicator st={saveStatus.km} /></div>
                {endEmpty && (
                  <p className="mt-2 text-[10px] text-slate-500 text-center">
                    Enter just your start reading now and save — come back at the end of the shift to add the end reading.
                  </p>
                )}
              </>
            )}
          </div>
        );
      })()}

      {/* Expense claims */}
      <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-4" data-testid="worker-expenses-section">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-emerald-300/80">
            <DollarSign className="h-3.5 w-3.5" /> Expense Claims
          </div>
          {lk("expenses") && <LockedPill />}
        </div>
          {returnedFeedback("expenses")}
        {(s.expense_claims || []).length > 0 && (
          <div className="space-y-2 mb-3">
            {s.expense_claims.map((e, i) => {
              const style = e.status === "Approved" ? "text-emerald-300 border-emerald-500/30 bg-emerald-500/10"
                : e.status === "Rejected" ? "text-red-300 border-red-500/30 bg-red-500/10"
                : "text-amber-300 border-amber-500/30 bg-amber-500/10";
              return (
                <div key={i} className="rounded-xl bg-black/20 border border-white/10 p-3" data-testid={`worker-expense-${i}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-white font-semibold text-sm">${Number(e.amount).toFixed(2)}</div>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${style}`}>{e.status}</span>
                  </div>
                  <div className="text-xs text-slate-300 mt-1">{e.reason}</div>
                  {e.has_receipt && <div className="mt-1.5"><ReceiptThumb shiftName={name} idx={i} name={e.receipt_name} size={60} /></div>}
                  {e.status === "Rejected" && e.reject_reason && <div className="text-[10px] text-red-300 mt-1">Rejected: {e.reject_reason}</div>}
                  {e.status === "Pending" && !lk("expenses") && (
                    <button onClick={() => deleteExpense(i)} data-testid={`worker-expense-delete-${i}`}
                      className="text-[10px] text-slate-400 hover:text-red-300 mt-1.5 flex items-center gap-1"><Trash2 className="h-3 w-3" /> Remove</button>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {!lk("expenses") && (
        <div className="space-y-2 rounded-xl border border-dashed border-white/15 p-3">
          <div className="text-[11px] font-semibold text-white/80">Add a claim</div>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
              <Input type="number" step="0.01" min="0" value={expForm.amount} onChange={(e) => setExpForm((f) => ({ ...f, amount: e.target.value }))}
                data-testid="worker-expense-amount" placeholder="0.00"
                className="pl-6 bg-black/20 border-white/10 text-white placeholder:text-slate-500" />
            </div>
          </div>
          <Input value={expForm.reason} onChange={(e) => setExpForm((f) => ({ ...f, reason: e.target.value }))}
            data-testid="worker-expense-reason" placeholder="Reason (e.g. parking, activity fee)"
            className="bg-black/20 border-white/10 text-white placeholder:text-slate-500 text-sm" />
          <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer" data-testid="worker-expense-file-label">
            <span className="flex items-center gap-1.5 rounded-md border border-white/15 px-3 py-2 hover:bg-white/10">
              <Upload className="h-3.5 w-3.5" /> {expForm.file ? "Change receipt" : "Attach receipt"}
            </span>
            <span className="text-slate-500 truncate">{expForm.file?.name || "required"}</span>
            <input type="file" accept="image/*,application/pdf" className="hidden" data-testid="worker-expense-file"
              onChange={(e) => setExpForm((f) => ({ ...f, file: e.target.files?.[0] || null }))} />
          </label>
          <Button onClick={addExpense} disabled={busy === "expense"} data-testid="worker-expense-submit"
            className="w-full h-10 font-semibold bg-brand hover:bg-brand-dark">
            {busy === "expense" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit Expense Claim"}
          </Button>
        </div>
        )}
      </div>

      {/* Incident reporting */}
      {s.return_info?.keys?.includes("incidents") && <div className="mt-4">{returnedFeedback("incidents")}</div>}
      <IncidentForm shiftName={name} participants={s.participants || []} incidents={s.incidents || []} onSaved={load} locked={lk("incidents")}
        office={{ business: s.office_phone_business, afterhours: s.office_phone_afterhours, pin: s.office_afterhours_pin }} />

      {/* Submit / status */}
      {s.actual_clock_out && !s.submitted && (
        s.is_linked && !s.is_last_leg && s.timesheet_status !== "Returned" ? (
          <div className="mt-5 rounded-2xl border border-indigo-400/30 bg-indigo-500/10 p-4" data-testid="worker-submit-linked-notice">
            <div className="text-[12px] text-indigo-100/90 mb-2.5">This is leg {s.link_seq} of a linked package. You submit the timesheet for <span className="text-white font-semibold">all {s.leg_count} legs together from the final leg</span> — make sure this leg's notes, KM and incidents are complete first.</div>
            <button onClick={() => nav(`/worker/shift/${s.last_leg}`)} data-testid="worker-goto-last-leg-submit"
              className="w-full h-11 rounded-md font-semibold bg-indigo-500/20 text-indigo-100 border border-indigo-400/40">
              Go to the final leg to submit →
            </button>
          </div>
        ) : (
          <div className="mt-5 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4" data-testid="worker-submit-timesheet-section">
            <div className="text-[12px] text-slate-300 mb-2.5">{s.is_linked && s.timesheet_status !== "Returned"
              ? <>Submitting locks and sends <span className="text-white font-semibold">every leg of this linked package ({s.leg_count} shifts)</span> for review. Complete all legs' notes, KM &amp; incidents first.</>
              : <>When your paperwork is complete, submit your timesheet. This <span className="text-white font-semibold">locks your notes, KM, expense claims and incident reports</span> and sends them to the office for review.</>}</div>
            <Button onClick={submitTimesheet} disabled={busy === "submit"} data-testid="worker-submit-timesheet"
              className="w-full h-12 text-base font-bold bg-brand hover:bg-brand-dark">
              {busy === "submit" ? <Loader2 className="h-5 w-5 animate-spin" /> : (s.is_linked && s.timesheet_status !== "Returned" ? `Submit Linked Package (${s.leg_count} shifts)` : "Submit Timesheet")}
            </Button>
          </div>
        )
      )}
      {s.submitted && (
        <div className="mt-5 rounded-2xl border border-white/10 bg-white/5 p-4 text-center" data-testid="worker-submitted-state">
          <div className="text-sm font-semibold text-white flex items-center justify-center gap-2">
            <Lock className="h-4 w-4 text-emerald-400" />
            {s.timesheet_status === "Approved" ? "Approved" : s.timesheet_status === "Flagged" ? "Returned for changes" : "Submitted for review"}
          </div>
          <div className="text-[11px] text-slate-400 mt-1">
            {s.timesheet_status === "Flagged" ? "See the banner above for what to fix, then it will re-lock." : "Your timesheet is locked. The office can return items if changes are needed."}
          </div>
        </div>
      )}

      {/* Participant quick link */}
      {s.participant && (
        <button onClick={() => nav(`/worker/participant/${s.participant}`)} data-testid="worker-shift-participant-link"
          className="mt-4 w-full flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 p-4 text-left transition-colors">
          <div className="h-9 w-9 rounded-full bg-emerald-500/20 flex items-center justify-center"><User className="h-4 w-4 text-[#00c86e]" /></div>
          <div className="flex-1">
            <div className="text-white text-sm font-medium">{s.participant_name}</div>
            <div className="text-[11px] text-slate-400">View participant details & notes</div>
          </div>
        </button>
      )}
      {s.masked && (
        <div className="mt-4 w-full flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 p-4" data-testid="worker-shift-detail-masked">
          <div className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center"><Lock className="h-4 w-4 text-slate-300" /></div>
          <div className="flex-1">
            <div className="text-white text-sm font-medium">Participant details hidden</div>
            <div className="text-[11px] text-slate-400">Full details (name, notes and care info) become visible closer to the shift, or once you're a preferred worker.</div>
          </div>
        </div>
      )}
      {pd.get_to_know_me && (
        <div className="mt-3 rounded-2xl border border-white/10 bg-white/5 p-4">
          <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-300/80 mb-1.5">Get to know them</div>
          <p className="text-xs text-slate-300 leading-relaxed whitespace-pre-wrap">{pd.get_to_know_me}</p>
        </div>
      )}

      {/* Geofence confirm */}
      <Dialog open={!!geoConfirm} onOpenChange={(v) => !v && setGeoConfirm(null)}>
        <DialogContent className="max-w-sm" data-testid="geo-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2 text-amber-600"><AlertTriangle className="h-4 w-4" />You're away from the shift</DialogTitle>
          </DialogHeader>
          {geoConfirm && (
            <p className="text-sm text-slate-600">
              You appear to be about <b>{fmtDistance(geoConfirm.distance)}</b> from the {geoConfirm.useEnd ? "end address" : "shift address"}. This will be recorded for the office. Do you still want to clock {geoConfirm.dir}?
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setGeoConfirm(null)} data-testid="geo-confirm-cancel">Cancel</Button>
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700" data-testid="geo-confirm-continue"
              onClick={() => doClock(geoConfirm.dir, geoConfirm.pos)}>
              Yes, clock {geoConfirm?.dir}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
